import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

const SHOW_DELAY_MS = 90;
const MIN_VISIBLE_MS = 260;
const SETTLE_DELAY_MS = 140;
const MAX_HOLD_MS = 2600;
const TRICKLE_INTERVAL_MS = 120;
const TRICKLE_CAP = 88;
const COMPLETE_WIDTH_MS = 280;
const FADE_OUT_MS = 180;
const LOADING_WIDTH_MS = 260;

type Phase = 'idle' | 'loading' | 'fading';

/** YouTube-style top loading bar for learner route transitions. */
export default function LearnerTopLoadingBar() {
  const location = useLocation();
  const isFetching = useIsFetching();
  const [mounted, setMounted] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);

  const mountedRef = useRef(false);
  const startedAtRef = useRef<number>(0);
  const transitionIdRef = useRef(0);
  const didInitRef = useRef(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxHoldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trickleIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = (timer: ReturnType<typeof setTimeout> | null) => {
    if (timer) clearTimeout(timer);
  };

  const clearAllTimers = useCallback(() => {
    clearTimer(showTimerRef.current);
    clearTimer(settleTimerRef.current);
    clearTimer(maxHoldTimerRef.current);
    clearTimer(completeTimerRef.current);
    clearTimer(fadeTimerRef.current);
    if (trickleIntervalRef.current) {
      clearInterval(trickleIntervalRef.current);
      trickleIntervalRef.current = null;
    }
    showTimerRef.current = null;
    settleTimerRef.current = null;
    maxHoldTimerRef.current = null;
    completeTimerRef.current = null;
    fadeTimerRef.current = null;
  }, []);

  useEffect(() => {
    mountedRef.current = mounted;
  }, [mounted]);

  const finishTransition = useCallback((transitionId: number) => {
    if (transitionId !== transitionIdRef.current) return;

    clearTimer(settleTimerRef.current);
    settleTimerRef.current = null;
    clearTimer(maxHoldTimerRef.current);
    maxHoldTimerRef.current = null;
    if (trickleIntervalRef.current) {
      clearInterval(trickleIntervalRef.current);
      trickleIntervalRef.current = null;
    }

    if (!mountedRef.current) {
      setProgress(0);
      setPhase('idle');
      return;
    }

    setProgress(100);
    completeTimerRef.current = setTimeout(() => {
      if (transitionId !== transitionIdRef.current) return;
      setPhase('fading');
      fadeTimerRef.current = setTimeout(() => {
        if (transitionId !== transitionIdRef.current) return;
        setMounted(false);
        setPhase('idle');
        setProgress(0);
      }, FADE_OUT_MS);
    }, COMPLETE_WIDTH_MS);
  }, []);

  const startTransition = useCallback(() => {
    clearAllTimers();

    transitionIdRef.current += 1;
    const transitionId = transitionIdRef.current;
    startedAtRef.current = Date.now();

    setMounted(false);
    setPhase('loading');
    setProgress(0);

    showTimerRef.current = setTimeout(() => {
      if (transitionId !== transitionIdRef.current) return;
      setMounted(true);
      setProgress(12);

      trickleIntervalRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= TRICKLE_CAP) return prev;
          const remaining = TRICKLE_CAP - prev;
          const increment = Math.max(0.35, remaining * (0.04 + Math.random() * 0.03));
          return Math.min(TRICKLE_CAP, prev + increment);
        });
      }, TRICKLE_INTERVAL_MS);
    }, SHOW_DELAY_MS);

    maxHoldTimerRef.current = setTimeout(() => {
      finishTransition(transitionId);
    }, MAX_HOLD_MS);
  }, [clearAllTimers, finishTransition]);

  useEffect(() => {
    if (!didInitRef.current) {
      didInitRef.current = true;
      return;
    }
    const kickoff = window.setTimeout(() => {
      startTransition();
    }, 0);
    return () => window.clearTimeout(kickoff);
  }, [location.pathname, location.search, location.hash, startTransition]);

  useEffect(() => {
    if (phase !== 'loading') return;

    if (isFetching > 0) {
      clearTimer(settleTimerRef.current);
      settleTimerRef.current = null;
      return;
    }

    const elapsed = Date.now() - startedAtRef.current;
    const waitMs = Math.max(MIN_VISIBLE_MS - elapsed, 0) + SETTLE_DELAY_MS;
    clearTimer(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      finishTransition(transitionIdRef.current);
    }, waitMs);
  }, [isFetching, phase, finishTransition]);

  useEffect(() => () => clearAllTimers(), [clearAllTimers]);

  if (!mounted && phase === 'idle') return null;

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-[3px]"
      style={{
        opacity: phase === 'fading' ? 0 : 1,
        transition: `opacity ${FADE_OUT_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`,
      }}
    >
      <div
        className="absolute inset-0"
        style={{
          background: 'var(--loading-track, rgba(92, 88, 112, 0.26))',
        }}
      />
      <div
        className="absolute top-0 left-0 h-full"
        style={{
          width: `${progress}%`,
          background: 'var(--color-brand-gradient)',
          boxShadow: '0 0 10px var(--loading-glow, rgba(255, 110, 177, 0.4))',
          transition:
            phase === 'loading'
              ? `width ${LOADING_WIDTH_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
              : `width ${COMPLETE_WIDTH_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`,
          willChange: 'width',
        }}
      />
    </div>
  );
}
